// File: packages/core/src/services/sessionService.js
import authService from './authService';
import config from '../config';

export const createOrResumeSession = async (chatSessionIdToResume = null, options = {}, signal = null) => {
  const url = `${config.API_BASE_URL}/api/v1/sessions`;
  const requestBody = chatSessionIdToResume ? { session_id_to_load: chatSessionIdToResume } : {};
  if (!chatSessionIdToResume) {
    if (options.team_name) {
      requestBody.team_name = options.team_name;
    }
    if (options.context && Array.isArray(options.context)) {
      requestBody.context = options.context;
    }
  }
  const action = chatSessionIdToResume ? "Resuming" : "Creating new";
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: requestBody,
      signal: signal,
    });
    if (!response.ok) {
      let errorMessage = `Failed to ${action.toLowerCase()} session`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || `${errorMessage}. Status: ${response.status}`;
      } catch (e) {
        errorMessage = `${errorMessage}. Status: ${response.status}`;
      }
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }
    const sessionData = await response.json();
    return sessionData;
  } catch (error) {
    if (error.name === 'AbortError') {
        console.log(`[sessionService] ${action} session request was aborted.`);
    } else {
        console.error(`[sessionService] Error ${action.toLowerCase()} session:`, error);
    }
    throw error;
  }
};

export const createSession = async () => {
    return createOrResumeSession(null);
};

export const sendMessage = async (sessionId, finalMessageContent) => {
  const url = `${config.API_BASE_URL}/api/v1/sessions/${sessionId}/messages`;
  try {
    console.log(`Sending message to session ${sessionId}...`);
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: { content: finalMessageContent }
    });
    if (!response.ok) {
      let errorMessage = 'Failed to send message';
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || `Failed to send message. Status: ${response.status}`;
      } catch (e) {
        errorMessage = `Failed to send message. Status: ${response.status}`;
      }
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }
    return await response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const getSessionStatus = async (sessionId) => {
  const url = `${config.API_BASE_URL}/api/v1/sessions/${sessionId}/status`;
  try {
    console.log(`Getting status for session ${sessionId}...`);
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorMessage = 'Failed to get session status';
      if (response.status === 404) {
          errorMessage = `Session ${sessionId} not found.`;
      } else {
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || `Failed to get session status. Status: ${response.status}`;
          } catch (e) {
            errorMessage = `Failed to get session status. Status: ${response.status}`;
          }
      }
      throw new Error(errorMessage);
    }
    return await response.json();
  } catch (error) {
    console.error('Error getting session status:', error);
    throw error;
  }
};

export const getSessionEventsUrl = (sessionId) => {
  const baseUrl = config.API_BASE_URL.endsWith('/') ? config.API_BASE_URL.slice(0, -1) : config.API_BASE_URL;
  return `${baseUrl}/api/v1/sse/events/${sessionId}`;
};

export const terminateSession = async (sessionId) => {
  if (!sessionId) {
    console.warn("Terminate session called without sessionId");
    return false;
  }
  const url = `${config.API_BASE_URL}/api/v1/sessions/${sessionId}`;
  try {
    console.log(`Attempting to terminate session ${sessionId}...`);
    const response = await authService.authFetch(url, { method: 'DELETE' });
    if (response.status === 204) {
      console.log(`Session ${sessionId} terminated successfully (backend acknowledged).`);
      return true;
    } else if (response.status === 404) {
      console.warn(`Session ${sessionId} not found on backend during termination attempt.`);
      return true;
    } else {
      let errorMessage = `Failed to terminate session ${sessionId}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || `Failed to terminate session. Status: ${response.status}`;
      } catch (e) {
        errorMessage = `Failed to terminate session. Status: ${response.status}`;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error(`Error terminating session ${sessionId}:`, error);
    throw error;
  }
};

export default {
  createOrResumeSession,
  createSession,
  sendMessage,
  getSessionStatus,
  getSessionEventsUrl,
  terminateSession
};